import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addToCart,
  registerCheckout,
  fetchCheckouts,
  fetchProductDetail,
  fetchUserAssetBalances,
  selectIsUserLoggedIn,
  selectUserInfo,
  selectWithcookieData,
  selectProductDetailState,
  selectUserAssetBalances,
} from "../../../redux/WithcookieSlice";
import { showToast } from "../../common/Toast";
import "./CustomMembershipSignup.css";
import { useTranslation } from "../../../redux/TranslationHelper";
import { adaptCustomMembershipSignupProps } from "./CustomMembershipSignup.adapter";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { CustomMembershipSignupProps } from "./CustomMembershipSignup.types";

// 임시로 response 타입 정의
interface CheckoutResponse {
  checkouts?: any[];
  [key: string]: any;
}


const CustomMembershipSignupComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = useMemo(
    () => adaptCustomMembershipSignupProps(props),
    [props]
  );

  // useTranslation을 컴포넌트 내부로 이동
  const { t } = useTranslation();

  const {
    productId = 0,
    title = "",
    description = "",
    buttonText = "",
    completedText = "",
    showRefundVisualization = true,
    refundLimitPercent = 400,
    assetOptions = [],
    minAmount = 10000,
    allowRejoin = true,
    className = "membership-signup-wrapper",
    style = {},
    isPreview = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    componentProps = {},
  } = adaptedProps;

  const dispatch = useDispatch() as any; // Redux 타입 호환성 위해 any 사용

  // Redux 상태
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const userInfo = useSelector(selectUserInfo);
  const withcookieData = useSelector(selectWithcookieData);
  const {
    productData,
    loading: productLoading,
    error: productError,
  } = useSelector(selectProductDetailState);
  const {
    balances: userAssetBalances,
    loading: userAssetBalancesLoading,
    error: userAssetBalancesError,
  } = useSelector(selectUserAssetBalances);

  // API 호출 추적을 위한 ref
  const assetApiCalledRef = useRef(false);

  // 상태
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [amount, setAmount] = useState("");
  const [assetInputs, setAssetInputs] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refundPercent, setRefundPercent] = useState(0);
  const [refundPercentOf, setRefundPercentOf] = useState(0);
  const [totalReturnValue, setTotalReturnValue] = useState(0);
  const [totalPV, setTotalPV] = useState(0);
  const [checkouts, setCheckouts] = useState<any[]>([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [individualCheckouts, setIndividualCheckouts] = useState<any[]>([]);
  const [hasExistingPayment, setHasExistingPayment] = useState(false);
  const [existingPaymentAmount, setExistingPaymentAmount] = useState(0);

  // 프리뷰용 상태 (프리뷰는 실제 배포된 환경, 에디터는 웹빌더 내부용)
  // isPreview가 true일 때는 실제 사용자가 보는 화면
  // isPreview가 false일 때는 웹빌더 에디터에서 보이는 화면
  const [previewAmount, setPreviewAmount] = useState("");
  const [previewAssetInputs, setPreviewAssetInputs] = useState<
    Record<string, string>
  >({});
  const [previewCompleted, setPreviewCompleted] = useState(false);
  const [previewRefundPercent, setPreviewRefundPercent] = useState(0);
  const [previewTotalReturnValue, setPreviewTotalReturnValue] = useState(0);
  const [previewTotalPV, setPreviewTotalPV] = useState(0);

  // 페이지네이션 상태 추가
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // 번역 함수 메모이제이션
  const translate = useCallback((str: string): string => t(str), [t]);

  // 프리뷰 환급 퍼센트 계산
  useEffect(() => {
    if (!isPreview) return;

    // 환급률 직접 계산 (디버깅용)
    if (previewTotalPV > 0 && refundLimitPercent > 0) {
      // 소수점 3째자리에서 버림 처리
      const calculatedPercent = Math.floor((previewTotalReturnValue / previewTotalPV) * 100 * 1000) / 1000; // 환급률
      const calculatedPercentOf = Math.floor((calculatedPercent / refundLimitPercent) * 100 * 100) / 100; // 프로그레스바 비율: 소수점 2째자리까지 표현

      // 테스트용 - 여기에도 값을 직접 설정
      setPreviewRefundPercent(calculatedPercentOf);
    }
  }, [previewTotalReturnValue, previewTotalPV, refundLimitPercent, isPreview]);

  // 미리보기 환급률 시각화 렌더링 (메모이제이션)
  const renderPreviewRefundVisualization = useCallback(() => {
    if (!showRefundVisualization) return null;

    const companyId = withcookieData?.company?.id || userInfo?.companyId;

    // 환급률 직접 계산 (렌더링할 때 마다 항상 최신 값으로 계산)
    let calculatedPercent = 0;
    let calculatedPercentOf = 0;
    if (previewTotalPV > 0 && refundLimitPercent > 0) {
      calculatedPercent = Math.floor((previewTotalReturnValue / previewTotalPV) * 100 * 1000) / 1000; // 환급률
      calculatedPercentOf = Math.floor((calculatedPercent / refundLimitPercent) * 100 * 100) / 100; // 프로그레스바 비율: 소수점 2째자리까지 표현
    }

    // 계산된 값을 직접 사용하여 렌더링
    return (
      <div className="refund-visualization">
        {/* companyId가 27이 아닐 때만 환급률 바 표시 */}
        {companyId !== 27 && (
          <div className="refund-bar-bg">
            <div
              className="refund-bar"
              style={{ width: `${Math.min(calculatedPercentOf, 100)}%` }}
            ></div>
          </div>
        )}
        <div className="refund-text">
          {/* companyId가 27이 아닐 때만 환급률 텍스트 표시 */}
          {companyId !== 27 && (
            <>
              {t("환급률")}: {calculatedPercent.toFixed(2)}% /{" "}
              {refundLimitPercent}%<br />
            </>
          )}
          {/* 누적 투자액과 환급액은 항상 표시 */}
          {t("누적 투자액")}
          {": "}
          {previewTotalPV.toLocaleString()}
          {"$"}
          <br />
          {t("누적 환급액")}
          {": "}
          {previewTotalReturnValue.toLocaleString()}
          {"$"}
        </div>
      </div>
    );
  }, [
    showRefundVisualization,
    previewRefundPercent,
    refundLimitPercent,
    previewTotalReturnValue,
    previewTotalPV,
    t,
    withcookieData,
    userInfo,
  ]);

  // 상품 정보 조회 - 프리뷰/에디터 모두 필요
  useEffect(() => {
    if (productId) {
      dispatch(fetchProductDetail(productId));
    }
  }, [dispatch, productId]);

  // 자산 정보 불러오기 - 프리뷰/에디터 모두 필요(로그인된 경우)
  useEffect(() => {
    // 이제 프리뷰(실제 배포환경)에서도 API를 호출해야 함
    if (isUserLoggedIn) {
      dispatch(fetchUserAssetBalances());
    }
  }, [isUserLoggedIn, dispatch]); // isPreview 의존성 제거 - 모든 모드에서 호출

  // companyId가 27일 때 자동으로 500 설정
  useEffect(() => {
    const companyId = withcookieData?.company?.id || userInfo?.companyId;

    if (companyId === 27 && (userAssetBalances as any)?.assetBalances && Array.isArray((userAssetBalances as any)?.assetBalances) && ((userAssetBalances as any)?.assetBalances || []).length > 0) {
      // 일반 모드 자동 설정
      if (!isPreview) {
        const newAssetInputs: Record<string, string> = {};
        ((userAssetBalances as any)?.assetBalances || []).forEach((asset: any) => {
          // assetOptions에 포함된 자산만 설정
          const assetOptionsList = Array.isArray(assetOptions) ? assetOptions : [assetOptions];
          const isAvailable = assetOptionsList.some((option: any) => {
            if (option && typeof option === "object" && option.id !== undefined) {
              return String(option.id) === String(asset.assetTypeId);
            }
            return String(option) === String(asset.assetTypeId);
          });

          if (isAvailable) {
            newAssetInputs[asset.assetTypeId] = "500";
          }
        });
        setAssetInputs(newAssetInputs);
      }

      // 프리뷰 모드 자동 설정
      if (isPreview) {
        const validAssetOptions = assetOptions.filter((opt: any) => {
          const optionId = opt.id || opt;
          return (userAssetBalances as any)?.assetBalances && Array.isArray((userAssetBalances as any)?.assetBalances) && ((userAssetBalances as any)?.assetBalances || []).some(
            (asset: any) => String(asset.assetTypeId) === String(optionId)
          );
        });

        const newPreviewAssetInputs: Record<string, string> = {};
        validAssetOptions.forEach((opt: any) => {
          const optionId = opt.id || opt;
          newPreviewAssetInputs[optionId] = "500";
        });
        setPreviewAssetInputs(newPreviewAssetInputs);
      }
    }
  }, [withcookieData, userInfo, userAssetBalances, assetOptions, isPreview]);

  // 주문 내역 가져오기
  useEffect(() => {
    if (!productId || !isUserLoggedIn) return;

    setIsLoading(true);
    setError(null);

    dispatch(fetchCheckouts({ page: 1, take: 500 }))
      .unwrap()
      .then((response: CheckoutResponse) => {
        const allCheckouts =
          response && response.checkouts ? response.checkouts : [];

        // 상태가 FINISHED나 CANCELED가 아닌 checkout만 필터링하고, 해당 상품이 포함된 checkout만 필터
        const activeCheckouts = allCheckouts.filter(
          (checkout: any) =>
            checkout &&
            checkout.status !== 'FINISHED' &&
            checkout.status !== 'CANCELED' &&
            checkout.carts &&
            Array.isArray(checkout.carts) &&
            checkout.carts.some(
              (cart: any) =>
                cart && cart.product && cart.product.id === productId
            )
        );

        // 최신순으로 정렬 (created_at 기준)
        const sortedCheckouts = activeCheckouts.sort((a: any, b: any) => {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          return dateB - dateA; // 최신순 (내림차순)
        });

        setCheckouts(sortedCheckouts);
        setIndividualCheckouts(sortedCheckouts);

        // 기존 결제 내역 확인
        if (sortedCheckouts.length > 0) {
          setHasExistingPayment(true);
          // 기존 결제 총액 계산
          const totalExisting = sortedCheckouts.reduce((sum: number, checkout: any) => {
            return sum + Number(checkout.total_pv || 0);
          }, 0);
          setExistingPaymentAmount(totalExisting);
        } else {
          setHasExistingPayment(false);
          setExistingPaymentAmount(0);
        }

        if (sortedCheckouts.length === 0) {
          setTotalReturnValue(0);
          setTotalPV(0);
          setRefundPercent(0);
          setRefundPercentOf(0);

          // isPreview인 경우에도 초기화
          if (isPreview) {
            setPreviewTotalReturnValue(0);
            setPreviewTotalPV(0);
            setPreviewRefundPercent(0);
          }

          setError(null);
          return;
        }

        // 전체 환급 퍼센트 계산 (기존 로직 유지)
        let returnSum = 0;
        let pvSum = 0;

        sortedCheckouts.forEach((chk: any) => {
          const returnValue = Number(chk.returnValue || 0);
          const totalPv = Number(chk.total_pv || 0);

          returnSum += returnValue;
          pvSum += totalPv;
        });

        setTotalReturnValue(returnSum);
        setTotalPV(pvSum);

        // isPreview인 경우에도 같은 값 설정
        if (isPreview) {
          setPreviewTotalReturnValue(returnSum);
          setPreviewTotalPV(pvSum);
        }

        let percent = 0;
        let percentOf = 0;

        if (pvSum > 0 && refundLimitPercent > 0) {
          // 소수점 3째자리에서 버림 처리
          percent = Math.floor((returnSum / pvSum) * 100 * 1000) / 1000; // 환급률: (누적환급액/누적투자액) * 100
          percentOf = Math.floor((percent / refundLimitPercent) * 100 * 100) / 100; // 프로그레스바 비율: 소수점 2째자리까지 표현
        }

        setRefundPercent(percent);
        setRefundPercentOf(percentOf);
        setError(null);
      })
      .catch((err: any) => {
        console.error("주문 내역 조회 에러:", err);
        setError(t("주문 내역을 불러오는데 실패했습니다."));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, productId, isUserLoggedIn, t, refundLimitPercent, isPreview]);

  // 금액 입력 핸들러 (메모이제이션)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, "");
      setAmount(value);
    },
    []
  );

  // 자산별 금액 입력 핸들러 (메모이제이션)
  const handleAssetInputChange = useCallback(
    (assetId: string, value: string, maxAmount: number) => {
      const companyId = withcookieData?.company?.id || userInfo?.companyId;

      // companyId가 27이면 500만 허용
      if (companyId === 27) {
        const cleanValue = value.replace(/[^0-9]/g, "");
        if (cleanValue !== "" && cleanValue !== "500") {
          showToast(t("500 달러 패키지 입니다."), "warning");
          return;
        }
        setAssetInputs((prev) => ({ ...prev, [assetId]: cleanValue }));
        return;
      }

      // 기존 로직
      let cleanValue = value.replace(/[^0-9]/g, "");
      if (Number(cleanValue) > Number(maxAmount)) {
        cleanValue = maxAmount.toString();
      }
      setAssetInputs((prev) => ({ ...prev, [assetId]: cleanValue }));
    },
    [withcookieData, userInfo, t]
  );

  // 자산별 입력값 합계 계산 (메모이제이션)
  const assetTotal = useMemo(() => {
    return Object.entries(assetInputs).reduce(
      (sum, [_, v]) => sum + Number(v || 0),
      0
    );
  }, [assetInputs]);

  // 결제 처리 (메모이제이션)
  const handleSubmit = useCallback(
    async (e: React.FormEvent | Event) => {
      // Event 객체가 있으면 preventDefault 호출
      if (e && typeof (e as any).preventDefault === "function") {
        (e as any).preventDefault();
      }

      setError(null);

      if (!isUserLoggedIn) {
        showToast(t("로그인이 필요합니다."), "warning");
        return;
      }

      // companyId가 27인 경우 500 체크
      const companyId = withcookieData?.company?.id || userInfo?.companyId;
      if (companyId === 27) {
        const hasNon500Amount = Object.values(assetInputs).some(value =>
          value !== "" && value !== "500"
        );
        if (hasNon500Amount || assetTotal !== 500) {
          showToast(t("500 달러 패키지 입니다."), "error");
          return;
        }
      }

      // 상품 정보 - API에서 이미 받은 응답 사용
      const product = productData?.product;
      if (!product) {
        showToast(t("상품 정보를 불러올 수 없습니다."), "error");
        return;
      }

      // 자산별 결제 금액 합산
      let payments: any[] = [];
      if ((userAssetBalances as any)?.assetBalances && Array.isArray((userAssetBalances as any)?.assetBalances)) {
        ((userAssetBalances as any)?.assetBalances || []).forEach((opt: any) => {
          const v = parseInt(assetInputs[opt.assetTypeId] || "0", 10);
          if (v > 0) {
            payments.push({
              method: "ASSET",
              assetTypeId: opt.assetTypeId,
              amount: v,
            });
          }
        });
      }

      if (payments.length === 0) {
        setError(t("결제 수단을 입력하세요."));
        return;
      }

      setIsLoading(true);

      try {
        // 1. 장바구니 추가 API 호출 (withcookieSlice의 addToCart 액션 사용)

        const cartParams = {
          product_id: product.id,
          count: 1,
          options: { custom_price: assetTotal.toString() },
        };

        const cartRes = await dispatch(addToCart(cartParams)).unwrap();

        const cartId =
          cartRes && cartRes.cart && cartRes.cart.id ? cartRes.cart.id : null;
        if (!cartId) {
          console.error("장바구니 추가 결과에 cartId가 없음:", cartRes);
          throw new Error(
            "장바구니 추가 실패 - 장바구니 ID를 찾을 수 없습니다."
          );
        }

        // 지불 방법 결정 (자산 유형이 여러 개면 'MIXED' 아니면 'ASSET')
        const paymentMethod = payments.length > 1 ? "MIXED" : "ASSET";

        // 2. 결제 진행 API 호출 (withcookieSlice의 registerCheckout 액션 사용)

        const checkoutData = {
          // cart_ids와 carts 둘 다 제공
          cart_ids: [cartId],
          carts: [
            {
              product_id: product.id,
              company_id:
                withcookieData?.company?.id || userInfo.companyId || 19,
              user_id: userInfo.id,
              count: 1,
            },
          ],
          payments,
          user_id: userInfo.id,
          company_id: withcookieData?.company?.id || userInfo.companyId || 19,
          skin_id: withcookieData?.skin?.id || 1,
          receiver_name: userInfo.name || "",
          receiver_phone: userInfo.phone || "",
          receiver_email: userInfo.email || "",
          address: userInfo.address || "",
          address_detail: "",
          postal_code: "",
          // 금액 정보 추가
          total_price: assetTotal,
          buy_price: assetTotal,
          parcel_price: 0,
          // 결제 방법 추가
          payment_method: paymentMethod,
        };

        const result = await dispatch(registerCheckout(checkoutData)).unwrap();

        // 결제 완료 처리
        setIsCompleted(true);
        showToast(t("멤버십 가입이 완료되었습니다."), "success");

        // 입력값 초기화
        setAssetInputs({});

        // 자산 정보와 구매 내역 다시 불러오기
        // 먼저 자산 정보 불러오기
        dispatch(fetchUserAssetBalances())
          .then(() => {
            // 그 다음 주문 내역 불러오기
            return dispatch(fetchCheckouts({ page: 1, take: 500 }));
          })
          .then(() => {
            // 데이터 업데이트 후 2초 뒤에 페이지 새로고침 (시간 증가)
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          })
          .catch((err: Error) => {
            console.error("데이터 업데이트 중 오류 발생:", err);
          });
      } catch (err: any) {
        console.error("결제 처리 오류:", err); // 에러 상세 로깅

        // 에러 메시지 추출 - response.data.message 형태로 오는 경우 처리
        let errorMessage = t("결제 처리 중 오류가 발생했습니다.");

        if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        showToast(errorMessage, "error");
      } finally {
        setIsLoading(false);
      }
    },
    [
      assetTotal,
      isUserLoggedIn,
      userAssetBalances,
      productData,
      t,
      assetInputs,
      dispatch,
      userInfo,
      withcookieData,
    ]
  );

  // 미리보기 렌더링 핸들러 (메모이제이션)
  const handlePreviewAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, "");
      setPreviewAmount(value);
    },
    []
  );

  const handlePreviewAssetInputChange = useCallback(
    (assetId: string, value: string) => {
      const companyId = withcookieData?.company?.id || userInfo?.companyId;

      // companyId가 27이면 500만 허용
      if (companyId === 27) {
        const cleanValue = value.replace(/[^0-9]/g, "");
        if (cleanValue !== "" && cleanValue !== "500") {
          showToast(t("500 달러 패키지 입니다."), "warning");
          return;
        }
        setPreviewAssetInputs((prev) => ({ ...prev, [assetId]: cleanValue }));
        return;
      }

      // 기존 로직
      const cleanValue = value.replace(/[^0-9]/g, "");
      setPreviewAssetInputs((prev) => ({ ...prev, [assetId]: cleanValue }));
    },
    [withcookieData, userInfo, t]
  );

  // 프리뷰 모드에서의 제출 처리 (메모이제이션)
  const handlePreviewSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      // 자산별 입력값 합계 계산
      const previewTotal = Object.values(previewAssetInputs).reduce(
        (sum, val) => sum + Number(val || 0),
        0
      );

      // companyId가 27인 경우 500 체크
      const companyId = withcookieData?.company?.id || userInfo?.companyId;
      if (companyId === 27) {
        const hasNon500Amount = Object.values(previewAssetInputs).some(value =>
          value !== "" && value !== "500"
        );
        if (hasNon500Amount || previewTotal !== 500) {
          showToast(t("500 달러 패키지 입니다."), "error");
          return;
        }
      }

      // 최소 금액 확인
      if (previewTotal < minAmount) {
        showToast(t("최소 금액 이상 입력해주세요."), "warning");
        return;
      }

      // 프리뷰 모드에서도 실제 API 호출 로직 추가

      // 상품 정보 확인
      if (!productData?.product) {
        showToast(t("상품 정보를 불러올 수 없습니다."), "error");
        return;
      }

      // 자산별 결제 금액 설정
      let payments: any[] = [];
      if ((userAssetBalances as any)?.assetBalances && Array.isArray((userAssetBalances as any)?.assetBalances) && ((userAssetBalances as any)?.assetBalances || []).length > 0) {
        ((userAssetBalances as any)?.assetBalances || []).forEach((asset: any) => {
          const value = previewAssetInputs[asset.assetTypeId];
          if (value && Number(value) > 0) {
            payments.push({
              method: "ASSET",
              assetTypeId: asset.assetTypeId,
              amount: Number(value),
            });
          }
        });
      }

      // 결제 금액이 없으면 오류 표시
      if (payments.length === 0) {
        showToast(t("결제 수단을 입력하세요."), "warning");
        return;
      }

      // 1. 장바구니 추가 API 호출

      // 타입스크립트 오류 해결을 위한 타입 캐스팅
      const cartParams = {
        product_id: productData.product.id,
        count: 1,
        options: { custom_price: previewTotal.toString() },
      };

      dispatch(addToCart(cartParams))
        .unwrap()
        .then((cartRes: any) => {
          if (cartRes && cartRes.cart && cartRes.cart.id) {
            const cartId = cartRes.cart.id;

            // 2. 결제 진행 API 호출
            const paymentMethod = payments.length > 1 ? "MIXED" : "ASSET";

            const checkoutData = {
              cart_ids: [cartId],
              carts: [
                {
                  product_id: productData.product.id,
                  company_id:
                    withcookieData?.company?.id || userInfo.companyId || 19,
                  user_id: userInfo.id,
                  count: 1,
                },
              ],
              payments,
              user_id: userInfo.id,
              company_id:
                withcookieData?.company?.id || userInfo.companyId || 19,
              skin_id: withcookieData?.skin?.id || 1,
              receiver_name: userInfo.name || "",
              receiver_phone: userInfo.phone || "",
              receiver_email: userInfo.email || "",
              address: userInfo.address || "",
              address_detail: "",
              postal_code: "",
              total_price: previewTotal,
              buy_price: previewTotal,
              parcel_price: 0,
              payment_method: paymentMethod,
            };

            dispatch(registerCheckout(checkoutData))
              .unwrap()
              .then((result: any) => {
                // 완료 처리
                setPreviewCompleted(true);
                setPreviewAmount("");
                setPreviewAssetInputs({});

                // 자산 정보와 구매 내역 다시 불러오기
                dispatch(fetchUserAssetBalances());
                dispatch(fetchCheckouts({ page: 1, take: 500 }));

                // 페이지 새로고침 - 실제 사용자 환경에서 데이터 업데이트 위해
                setTimeout(() => {
                  window.location.reload();
                }, 1000);
              })
              .catch((err: any) => {
                console.error("프리뷰 모드 - 결제 실패:", err);

                // 에러 메시지 추출 - response.data.message 형태로 오는 경우 처리
                let errorMessage = t("결제 처리 중 오류가 발생했습니다.");

                if (err?.response?.data?.message) {
                  errorMessage = err.response.data.message;
                } else if (err?.data?.message) {
                  errorMessage = err.data.message;
                } else if (err?.message) {
                  errorMessage = err.message;
                }

                showToast(errorMessage, "error");
              });
          } else {
            console.error("프리뷰 모드 - 장바구니 ID 없음:", cartRes);
            showToast(t("장바구니 추가 실패"), "error");
          }
        })
        .catch((err: any) => {
          console.error("프리뷰 모드 - 장바구니 추가 실패:", err);
          showToast(t("장바구니 추가 중 오류가 발생했습니다."), "error");
        });
    },
    [
      previewAssetInputs,
      minAmount,
      t,
      productData,
      userAssetBalances,
      dispatch,
      withcookieData,
      userInfo,
    ]
  );

  // 환급률 시각화 렌더링 (메모이제이션)
  const renderRefundVisualization = useCallback(() => {
    if (!showRefundVisualization) return null;

    const companyId = withcookieData?.company?.id || userInfo?.companyId;
    return (
      <div className="refund-visualization">
        {/* companyId가 27이 아닐 때만 환급률 바와 텍스트 표시 */}
        {companyId !== 27 && (
          <>
            <div className="refund-bar-bg">
              <div
                className="refund-bar"
                style={{ width: `${Math.min(refundPercentOf, 100)}%` }}
              ></div>
            </div>
            <div className="refund-text">
              {t("환급률")}: {refundPercent.toFixed(3)}% /{" "}
              {refundLimitPercent}%<br />
            </div>
          </>
        )}
        {/* 누적 투자액, 누적 환급액은 항상 표시 */}
        <div className="refund-text">
          {t("누적 투자액")}
          {": "}
          {totalPV.toLocaleString()}
          {"$"}
          <br />
          {t("누적 환급액")}
          {": "}
          {totalReturnValue.toLocaleString()}
          {"$"}
        </div>
      </div>
    );
  }, [
    showRefundVisualization,
    refundPercentOf,
    refundPercent,
    refundLimitPercent,
    totalPV,
    totalReturnValue,
    t,
    withcookieData,
    userInfo,
  ]);

  // 개별 checkout 환급률 시각화 렌더링
  const renderIndividualCheckouts = useCallback(() => {
    if (!individualCheckouts || individualCheckouts.length === 0) return null;

    // 페이지네이션 계산
    const totalItems = individualCheckouts.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = individualCheckouts.slice(startIndex, endIndex);

    // 체크아웃 취소 핸들러
    const handleCancelCheckout = async (checkoutId: number) => {
      if (!window.confirm(t("정말로 이 투자를 취소하시겠습니까?"))) {
        return;
      }

      try {
        setIsLoading(true);

        // API 호출 - 체크아웃 상태를 CANCELED로 변경
        const response = await fetch(`/api/checkouts/${checkoutId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`,
          },
          body: JSON.stringify({
            status: 'CANCELED'
          })
        });



        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("API 오류 응답:", errorData);

          if (response.status === 403) {
            throw new Error(t(errorData.message || "해당 주문을 찾을 수 없습니다."));
          } else if (response.status === 401) {
            throw new Error(t("로그인이 필요합니다."));
          } else {
            throw new Error(errorData.message || t("취소 처리 중 오류가 발생했습니다."));
          }
        }

        const result = await response.json();

        // 성공 처리 - result.success 체크 없이 response.ok만으로 처리
        const canceledCheckout = result.data?.checkout || result.checkout || result;

        // 보상 지급 여부 확인 및 메시지 표시
        if ((canceledCheckout?.companyId === 24 || canceledCheckout?.companyId==26) && canceledCheckout?.total_pv > 0) {
          showToast(
            `${t("원금 회수가 완료되었습니다.")} ${canceledCheckout.total_pv} PV${t("가 보상으로 지급됩니다.")}`,
            "info"
          );
        } else {
          showToast(t("원금 회수가 완료되었습니다."), "info");
        }

        // 바로 페이지 새로고침

        window.location.reload();

      } catch (error: any) {
        console.error("원금 회수 오류:", error);
        showToast(error.message || t("원금 회수 중 오류가 발생했습니다."), "error");
        setIsLoading(false);
      }
    };

    // 페이지 변경 핸들러
    const handlePageChange = (page: number) => {
      setCurrentPage(page);
    };

    return (
      <div className="investment-history">
        <h3 className="history-title">{t("투자 내역")}</h3>

        <div className="investment-table-container">
          <table className="investment-table">
            <thead>
              <tr>
                <th>{t("투자금")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((checkout: any, index: number) => {
                const totalPv = Number(checkout.total_pv || 0);

                return (
                  <tr key={checkout.id || index}>
                    <td className="amount-cell">{totalPv.toLocaleString()}$</td>
                    <td className="action-cell">
                      {(checkout.companyId === 24 || checkout.companyId === 26) && (
                        <button
                          className="cancel-button"
                          onClick={() => handleCancelCheckout(checkout.id)}
                          disabled={isLoading || checkout.status === 'CANCELED'}
                        >
                          {t("원금 회수")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="pagination">
            <div className="pagination-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="pagination-info">
          {t("총")} {totalItems}{t("개 중")} {startIndex + 1}-{Math.min(endIndex, totalItems)}{t("개 표시")}
        </div>
      </div>
    );
  }, [individualCheckouts, currentPage, itemsPerPage, t, isLoading]);

  // 미리보기 모드 렌더링
  if (isPreview) {
    // 프리뷰 모드 = 실제 배포 환경에서 사용자에게 보여지는 화면

    // 재가입 불가 상태 체크 (프리뷰 모드에서도 동일하게 적용)
    if (!allowRejoin && hasExistingPayment) {
      return (
        <div className={className} style={style}>
          <div className="container">
            <h2 className="membership-title">{title}</h2>
            <p className="membership-description">{description}</p>
            <div className="error-message">
              <p>{t("이미 가입된 멤버십입니다. 재가입이 불가능합니다.")}</p>
              <p>{t("기존 투자액")}: {existingPaymentAmount.toLocaleString()}$</p>
            </div>
            {renderIndividualCheckouts()}
          </div>
        </div>
      );
    }

    // 유효한 자산 옵션만 필터링 (userAssetBalances에 있는 assetTypeId만 허용)
    const validAssetOptions = assetOptions.filter((opt: any) => {
      const optionId = opt.id || opt;
      return (userAssetBalances as any)?.assetBalances && Array.isArray((userAssetBalances as any)?.assetBalances) && ((userAssetBalances as any)?.assetBalances || []).some(
        (asset: any) => String(asset.assetTypeId) === String(optionId)
      );
    });

    return (
      <div className={className} style={style}>
        <div className="container">
          <h2 className="membership-title">{title}</h2>
          <p className="membership-description">{description}</p>
          {/* 재가입 허용이고 기존 결제가 있는 경우 표시 */}
          {allowRejoin && hasExistingPayment && (
            <div className="existing-payment-info">
              <p>{t("기존 투자액")}: {existingPaymentAmount.toLocaleString()}$</p>
            </div>
          )}
          {renderPreviewRefundVisualization()}
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            className="custom-membership-form"
          >
            {validAssetOptions && validAssetOptions.length > 0 ? (
              <div className="asset-inputs">
                <label>{t("자산별 결제 금액")}</label>
                {validAssetOptions.map((opt: any) => {
                  const optionId = opt.id || opt;
                  const assetBalances = (userAssetBalances as any)?.assetBalances || [];
                  const matchingAsset = Array.isArray(assetBalances) ? assetBalances.find(
                    (asset: any) =>
                      String(asset.assetTypeId) === String(optionId)
                  ) : null;

                  if (!matchingAsset) return null;

                  return (
                    <div key={optionId} className="asset-input-row">
                      <span>{matchingAsset.assetName || String(optionId)}</span>
                      <input
                        type="text"
                        value={previewAssetInputs[optionId] || ""}
                        onChange={(e) =>
                          handlePreviewAssetInputChange(
                            optionId.toString(),
                            e.target.value
                          )
                        }
                        placeholder={`${
                          matchingAsset.companyDisplayNames?.[21]
                            ?.displayName ||
                          matchingAsset.assetName ||
                          "Asset"
                        } (${t("사용 가능")}: ${
                          matchingAsset.availableAmount
                        })`}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-asset-message">
                <p>{t("결제할 수 있는 자산이 없습니다.")}</p>
              </div>
            )}
            <button
              className="signup-button"
              type="button"
              onClick={(e) => {
                // 실제 API 호출 함수 실행
                handlePreviewSubmit(new Event("submit") as any);
              }}
              disabled={
                Object.keys(previewAssetInputs).length === 0 ||
                Object.values(previewAssetInputs).reduce(
                  (sum, val) => sum + Number(val || 0),
                  0
                ) < minAmount
              }
            >
              {t(buttonText)}
            </button>
          </form>
          {previewCompleted && (
            <div className="signup-completed">
              <p>{t(completedText)}</p>
            </div>
          )}
          {renderIndividualCheckouts()}
        </div>
      </div>
    );
  }

  // 로딩 상태 렌더링
  if (isLoading || productLoading || userAssetBalancesLoading) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t("처리 중입니다...")}</p>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 렌더링 (체크아웃이 있는 경우)
  if (
    (error || productError) &&
    error !== null &&
    error !== "" &&
    checkouts &&
    checkouts.length > 0
  ) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="error-message">
            <p>{error || productError}</p>
            <button
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              {t("다시 시도")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 필요 상태 렌더링
  if (!isUserLoggedIn) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="login-required">
            <h2 className="membership-title">{title}</h2>
            <p>{t("멤버십 가입을 위해 로그인이 필요합니다.")}</p>
            <a href="/login" className="login-button">
              {t("로그인하기")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // 상품 정보 없음 상태 렌더링
  if (!productData || !productData.product) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="error-message">
            <h2 className="membership-title">{title}</h2>
            <p>{t("표시할 상품이 없습니다.")}</p>
          </div>
        </div>
      </div>
    );
  }

  // 자산 정보 에러 상태 렌더링
  if (userAssetBalancesError) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="error-message">{userAssetBalancesError}</div>
        </div>
      </div>
    );
  }

  // 자산 정보 없음 상태 렌더링
  if (!userAssetBalances || ((userAssetBalances as any)?.assetBalances || []).length === 0) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="error-message">
            {t("자산 정보를 불러올 수 없습니다.")}
          </div>
        </div>
      </div>
    );
  }

  // 구매 내역 없음 상태 렌더링
  if (!checkouts) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="error-message">
            <p>{t("구매내역이 없습니다.")}</p>
          </div>
        </div>
      </div>
    );
  }

  // 재가입 불가 상태 체크
  if (!allowRejoin && hasExistingPayment) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <h2 className="membership-title">{title}</h2>
          <p className="membership-description">{description}</p>
          <div className="error-message">
            <p>{t("이미 가입된 멤버십입니다. 재가입이 불가능합니다.")}</p>
            <p>{t("기존 투자액")}: {existingPaymentAmount.toLocaleString()}$</p>
          </div>
          {renderIndividualCheckouts()}
        </div>
      </div>
    );
  }

  // 정상 렌더링
  return (
    <div className={className} style={style}>
      <div className="container">
        <h2 className="membership-title">{title}</h2>
        <p className="membership-description">{description}</p>
        {/* 재가입 허용이고 기존 결제가 있는 경우 표시 */}
        {allowRejoin && hasExistingPayment && (
          <div className="existing-payment-info">
            <p>{t("기존 투자액")}: {existingPaymentAmount.toLocaleString()}$</p>
          </div>
        )}
        {renderRefundVisualization()}
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className="custom-membership-form"
        >
          {userAssetBalances && ((userAssetBalances as any)?.assetBalances || []).length > 0 ? (
            <div className="asset-inputs">
              <label>{t("자산별 결제 금액")}</label>
              {(() => {
                // assetOptions 형태 확인 (배열 또는 단일 값)
                const assetOptionsList = Array.isArray(assetOptions)
                  ? assetOptions
                  : [assetOptions];

                // assetOptions에 지정된 ID를 갖는 자산 필터링
                const availableAssets =
                  assetOptionsList.length > 0
                    ? ((userAssetBalances as any)?.assetBalances || []).filter((asset: any) => {
                        // 각 에셋 옵션과 비교
                        const isAvailable = assetOptionsList.some(
                          (option: any) => {
                            // 옵션이 객체인 경우 (예: {id: 4})
                            if (
                              option &&
                              typeof option === "object" &&
                              option.id !== undefined
                            ) {
                              return (
                                String(option.id) === String(asset.assetTypeId)
                              );
                            }
                            // 옵션이 단순 값인 경우 (예: 4)
                            return String(option) === String(asset.assetTypeId);
                          }
                        );

                        return isAvailable;
                      })
                    : []; // assetOptions가 비어있으면 빈 배열 반환

                if (availableAssets.length > 0) {
                  return availableAssets.map((asset: any) => (
                    <div key={asset.assetTypeId} className="asset-input-row">
                      <span>
                        {asset.assetName || String(asset.assetTypeId)}
                      </span>
                      <input
                        type="text"
                        value={assetInputs[asset.assetTypeId] || ""}
                        onChange={(e) =>
                          handleAssetInputChange(
                            String(asset.assetTypeId),
                            e.target.value,
                            Number(asset.availableAmount) || 0
                          )
                        }
                        placeholder={`${t(
                          asset.companyDisplayNames?.[21]?.displayName ||
                            asset.assetName ||
                            "Asset"
                        )} (${t("balance")}: ${asset.availableAmount})`}
                        disabled={isLoading}
                      />
                    </div>
                  ));
                } else {
                  return (
                    <div className="no-asset-message">
                      <p>{t("결제할 수 있는 자산이 없습니다.")}</p>
                    </div>
                  );
                }
              })()}
            </div>
          ) : (
            <div className="loading-assets">
              <p>{t("자산 정보를 불러오는 중입니다...")}</p>
            </div>
          )}
          <div className="asset-total">
            {t("total")}
            {": "}
            {assetTotal.toLocaleString()}
            {"$"}
          </div>
          <button
            className="signup-button"
            type="button"
            onClick={(e) => {
              if (!isLoading && assetTotal >= minAmount) {
                handleSubmit(new Event("submit") as any);
              } else {
              }
            }}
            disabled={isLoading || assetTotal < minAmount}
          >
            {t(buttonText)}
          </button>
        </form>
        {isCompleted && (
          <div className="signup-completed">
            <p>{t(completedText)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// displayName 추가
CustomMembershipSignupComponent.displayName = "자유 멤버십 가입";

export default CustomMembershipSignupComponent;
